// The panel and feature registry.
//
// A feature registers itself by adding a file src/**/register.ts whose default
// export is a Feature (use defineFeature for type checking). Nothing else lists
// features: the glob below finds every register.ts at build time, so a new
// feature never edits this file or main.ts.

import type { AudioSession } from "./app/controls/audio-session";
import type { ViewStore } from "./state/store";

/** Where the app shell mounts a feature. */
export type Slot = "stage" | "panel";

/** What a feature gets when it mounts. */
export interface FeatureContext {
  /** The feature's own container element, already in the document. */
  readonly root: HTMLElement;
  /** The view state: read it, subscribe to it, change it (docs/contracts/view-state.md). */
  readonly state: ViewStore;
  /** The app's audio: the running engine, its telemetry and its controls. */
  readonly audio: AudioSession;
}

/** Undoes a mount: removes listeners, stops loops, frees GPU and audio resources. */
export type Unmount = () => void;

export interface Feature {
  /** Unique, kebab-case. Becomes the container's data-feature attribute. */
  readonly id: string;
  /** Human-readable name, shown as the panel heading. */
  readonly title: string;
  /** "stage" for the main view, "panel" for the side panels. */
  readonly slot: Slot;
  /** Sort key within the slot; lower comes first. Ties sort by id. Default 100. */
  readonly order?: number;
  mount(ctx: FeatureContext): void | Unmount;
}

/** Identity helper that type-checks a register.ts default export. */
export function defineFeature(feature: Feature): Feature {
  return feature;
}

const ID_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

interface RegisterModule {
  default?: unknown;
}

/**
 * Checks and orders the features found in register.ts modules.
 * Exported separately from the glob so tests can feed it modules directly.
 */
export function collectFeatures(modules: Record<string, RegisterModule>): Feature[] {
  const byId = new Map<string, string>();
  const features: Feature[] = [];
  for (const [path, mod] of Object.entries(modules)) {
    const feature = mod.default;
    if (!isFeature(feature)) {
      throw new Error(`${path}: the default export is not a Feature (use defineFeature).`);
    }
    if (!ID_PATTERN.test(feature.id)) {
      throw new Error(`${path}: feature id "${feature.id}" is not kebab-case.`);
    }
    const previous = byId.get(feature.id);
    if (previous !== undefined) {
      throw new Error(`${path}: feature id "${feature.id}" is already registered by ${previous}.`);
    }
    byId.set(feature.id, path);
    features.push(feature);
  }
  return features.sort(
    (a, b) => (a.order ?? 100) - (b.order ?? 100) || a.id.localeCompare(b.id),
  );
}

function isFeature(value: unknown): value is Feature {
  if (typeof value !== "object" || value === null) return false;
  const f = value as Record<string, unknown>;
  return (
    typeof f.id === "string" &&
    typeof f.title === "string" &&
    (f.slot === "stage" || f.slot === "panel") &&
    (f.order === undefined || typeof f.order === "number") &&
    typeof f.mount === "function"
  );
}

/** Every registered feature, ordered. */
export const features: readonly Feature[] = collectFeatures(
  import.meta.glob<RegisterModule>("./**/register.ts", { eager: true }),
);
